import { z } from 'zod';
import { ParticipantLimitMode } from '@/features/dashboard/tourOptions/types/ParticipantLimitMode.ts';
import { init } from 'zod-empty';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { hasAgeRangeOverlap } from '@/schemas/ageRangeOverlapValidation.ts';

const ageRestrictionPriceSchema = z.object({
  // Pola przelotowe z DTO (nierenderowane): utrzymują tożsamość i stan
  // archiwizacji wiersza cennika między odczytem a zapisem (PUT).
  id: z.string().optional(),
  archived: z.boolean().optional(),
  minAge: z.coerce
    .number({
      error: 'Wiek musi być liczbą',
    })
    .int()
    .nonnegative('Wiek minimalny musi być większy lub równy 0')
    .optional(),
  maxAge: z.coerce
    .number({
      error: 'Wiek musi być liczbą',
    })
    .int()
    .positive('Wiek maksymalny musi być większy od 0')
    .optional(),
  price: z.coerce
    .number({
      error: 'Cena musi być liczbą',
    })
    .nonnegative('Cena musi być większa lub równa 0'),
});

const variantSchema = z
  .object({
    // Pola przelotowe z DTO (nierenderowane) — tożsamość wariantu i jego
    // ceny podstawowej oraz stan archiwizacji, zachowywane przy PUT.
    id: z.string().optional(),
    archived: z.boolean().optional(),
    basePriceId: z.string().optional(),
    basePriceArchived: z.boolean().optional(),
    name: z
      .string({ error: 'Nazwa wariantu jest wymagana' })
      .min(1, 'Nazwa wariantu jest wymagana')
      .max(255, 'Nazwa wariantu nie może przekraczać 255 znaków'),
    price: z.coerce
      .number({ error: 'Cena musi być liczbą' })
      .nonnegative('Cena nie może być ujemna'),
    isPriceByAge: z.boolean().default(false),
    ageRestrictionPrices: z.array(ageRestrictionPriceSchema),
    isVariantOnlyForDefinedAgeRanges: z.boolean().default(false),
    participantLimitMode: z.enum(ParticipantLimitMode, {
      error: 'Musisz wybrać limit rezerwacji',
    }),
    participantLimit: z.coerce
      .number({ error: 'Liczba mmiejsc musi być liczbą' })
      .positive('Liczba miejsc musi być większa od 0')
      .optional(),
  })
  .refine(
    (data) => {
      if (data.participantLimitMode === ParticipantLimitMode.PER_VARIANT) {
        return data.participantLimit && data.participantLimit > 0;
      }
      return true;
    },
    {
      message: 'Liczba miejsc musi być większa od 0',
      path: ['participantLimit'],
    },
  )
  .refine(
    (data) => {
      if (!data.isPriceByAge || data.ageRestrictionPrices.length < 2) {
        return true;
      }
      return !hasAgeRangeOverlap(data.ageRestrictionPrices);
    },
    {
      message: 'Zakresy wiekowe nie mogą się nakładać',
      path: ['ageRestrictionPrices'],
    },
  );

export const tourOptionBaseSChema = z.object({
  name: z
    .string({ error: 'Nazwa opcji jest wymagana' })
    .min(1, 'Nazwa opcji jest wymagana')
    .max(255, 'Nazwa opcji nie może przekraczać 255 znaków'),
  description: z.string().optional(),
});

export const tourOptionWihoutVarinastSchema = tourOptionBaseSChema.extend({});

export const tourOptionSchema = z
  .object({
    // Pola przelotowe z DTO (nierenderowane): stan archiwizacji opcji oraz —
    // dla opcji bez wariantów — tożsamość ukrytego wariantu bazowego i jego
    // ceny podstawowej, zachowywane przy PUT.
    archived: z.boolean().optional(),
    baseVariantId: z.string().optional(),
    baseVariantArchived: z.boolean().optional(),
    basePriceId: z.string().optional(),
    basePriceArchived: z.boolean().optional(),
    name: z
      .string({ error: 'Nazwa opcji jest wymagana' })
      .min(1, 'Nazwa opcji jest wymagana')
      .max(255, 'Nazwa opcji nie może przekraczać 255 znaków'),
    description: z.string().optional(),
    currency: z
      .enum(Currency, { error: 'Wybór waluty jest wymagany' })
      .nonoptional(),
    required: z.boolean().default(false),
    hasVariants: z.boolean().default(false),
    participantLimitMode: z
      .enum(ParticipantLimitMode, {
        error: 'Musisz wybrać limit rezerwacji',
      })
      .default(ParticipantLimitMode.NONE),
    participantLimit: z.coerce
      .number({
        error: 'Liczba miejsc musi być liczbą',
      })
      .int('Liczba miejsc nie może być ułamkiem')
      .optional(),

    isPriceByAge: z.boolean().default(false),
    isVariantOnlyForDefinedAgeRanges: z.boolean().default(false),
    basePrice: z
      .number({
        error: 'Cena podstawowa musi być liczbą',
      })
      .optional(),
    ageRestrictionPrices: z.array(ageRestrictionPriceSchema),
    allowOptionMultiSelect: z.boolean().default(false),
    variants: z.array(variantSchema),
  })
  .refine(
    (data) => {
      if (data.participantLimitMode === ParticipantLimitMode.PER_TOUR_OPTION) {
        return data.participantLimit && data.participantLimit > 0;
      }
      return true;
    },
    {
      message: 'Liczba miejsc musi być większa od 0',
      path: ['participantLimit'],
    },
  )
  .refine(
    (data) => {
      if (!data.isPriceByAge && !data.hasVariants) {
        return data.basePrice !== undefined && data.basePrice >= 0;
      }
      return true;
    },
    {
      message: 'Cena podstawowa jest wymagana',
      path: ['basePrice'],
    },
  )
  .refine(
    (data) => {
      if (data.isPriceByAge && data.ageRestrictionPrices.length === 0) {
        return false;
      }
      return true;
    },
    {
      message:
        'Musisz dodać co najmniej jedną cenę dla ograniczenia wiekowego.',
      path: ['ageRestrictionPrices'],
    },
  )
  .refine(
    (data) => {
      if (!data.isPriceByAge || data.ageRestrictionPrices.length < 2) {
        return true;
      }
      return !hasAgeRangeOverlap(data.ageRestrictionPrices);
    },
    {
      message: 'Zakresy wiekowe nie mogą się nakładać',
      path: ['ageRestrictionPrices'],
    },
  )
  .refine(
    (data) => {
      if (data.hasVariants && data.variants.length < 2) {
        return false;
      }
      return true;
    },
    {
      message: 'Warianty muszą mieć co najmniej 2 elementy',
      path: ['variants'],
    },
  )
  .transform((data) => {
    if (!data.hasVariants) {
      return { ...data, variants: [] };
    }
    return data;
  });

export type TourOptionFormData = z.infer<typeof tourOptionSchema>;
export type TourOptionInputForm = z.input<typeof tourOptionSchema>;
export type VariantFormData = z.infer<typeof variantSchema>;
export type AgeRestrictionPriceFormData = z.infer<
  typeof ageRestrictionPriceSchema
>;

export const tourOptionDefaultValues = init(tourOptionSchema);
